import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'
import { getObject } from '../../../api/apiClientCrud'
import { useLocation, useOutletContext } from 'react-router-dom'
import Toolbar from '../../toolbar/Toolbar'
import { BiSolidBusiness } from 'react-icons/bi'
import { TbPhoneFilled, TbMailFilled } from 'react-icons/tb'
import { FaLocationDot } from 'react-icons/fa6'
import SubModule from '../../module/SubModule'
import { useAuth } from '../../../context/AuthContext'
import AuditSubModule from '../../subModules/AuditSubModule'
import OutcomeSubModule from '../../subModules/OutcomeSubModule'
import NotFound from '../../common/NotFound'
import Loading from '../../common/Loading'

function ViewSupplier() {
  const { user } = useAuth()
  const userRight = useOutletContext()
  const location = useLocation()
  const supplierId = location?.state?.id

  const [supplier, setSupplier] = useState({})
  const [loading, setLoading] = useState(true)

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        return `Fournisseur ${supplier.supplier_name}`
      },
      objectType: 'suppliers',
      actions: { edit: true, back: true, list: true }
    }),
    [userRight, supplier]
  )

  const subModules = useMemo(
    () => ({
      tabsNames: [
        { tabName: 'Factures', tabIndex: '1' },
        { tabName: 'Audits', tabIndex: '2' }
      ],
      tabs: [
        {
          tabIndex: '1',
          component: () => <OutcomeSubModule userRight={userRight} user={user} location={location} />
        },
        {
          tabIndex: '2',
          component: () => (
            <AuditSubModule userRight={userRight} user={user} location={location} objectType='suppliers' />
          )
        }
      ]
    }),
    [userRight, user, location]
  )

  useEffect(() => {
    if (!supplierId) {
      setLoading(false)
      return
    }

    const fetchSupplier = async () => {
      try {
        const res = await getObject('suppliers', supplierId)
        setSupplier(res.data)
      } catch (err) {
        toast.error('Erreur lors du chargement', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchSupplier()
  }, [supplierId])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!supplier.id) return <NotFound title='Fournisseur introuvable' />

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbar} object={supplier} />

      <Paper sx={{ width: '90%', marginLeft: '5%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', margin: '2%' }}>
          <Box sx={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', marginRight: '5px' }}>
            <Typography sx={{ marginRight: '5px', marginBottom: '5px' }}>Fournisseur</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <BiSolidBusiness size={30} />
              <Typography sx={{ marginLeft: '5px' }}>{supplier.supplier_name}</Typography>
            </Box>

            {(supplier.phone || supplier.email) && (
              <>
                <Typography sx={{ marginRight: '5px', marginBottom: '5px', marginTop: '5px' }}>Contact</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {supplier.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginBottom: '2px' }}>
                      <TbPhoneFilled size={30} />
                      <Typography sx={{ marginLeft: '5px' }}>{supplier.phone}</Typography>
                    </Box>
                  )}
                  {supplier.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginBottom: '2px' }}>
                      <TbMailFilled size={30} />
                      <Typography sx={{ marginLeft: '5px' }}>{supplier.email}</Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', marginRight: '5px' }}>
            {(supplier.address1 || supplier.zip || supplier.city) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: '5px' }}>
                <Typography sx={{ marginRight: '5px' }}>Adresse</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                  <FaLocationDot size={30} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '5px' }}>
                    <Typography>{supplier.address1 || ''}</Typography>
                    <Typography>{supplier.address2 || ''}</Typography>
                    <Typography>
                      {supplier.zip || ''} {supplier.city || ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ width: '90%', marginLeft: '5%', marginTop: '10px', marginBottom: '10px' }}>
        <SubModule options={subModules} />
      </Paper>
    </Box>
  )
}

export default ViewSupplier
